export enum BILLING_RESOURCES_TYPES {
  INSTANCES,
  VOLUMES,
  DATABASES,
  S3,
  BACKUPS
}

export enum BILLING_ROLES {
  OWNER = "owner",
  ADMIN = "admin"
}

export const BILLING_ROLES_LABELS = {
  [BILLING_ROLES.ADMIN]: "Billing Admin",
  [BILLING_ROLES.OWNER]: "Owner"
};

export const BILLING_RESOURCES_TYPES_TITLES: {
  [key in BILLING_RESOURCES_TYPES]: string;
} = {
  [BILLING_RESOURCES_TYPES.INSTANCES]: "Instances",
  [BILLING_RESOURCES_TYPES.VOLUMES]: "Volumes",
  [BILLING_RESOURCES_TYPES.DATABASES]: "Databases",
  [BILLING_RESOURCES_TYPES.S3]: "Object Storage",
  [BILLING_RESOURCES_TYPES.BACKUPS]: "Backups"
};

// User Billing Data (organizations list)
export type UserOrganizationBillingData = {
  id: string;
  name: string;
  status: string;
  billing_admins: string[];
  billing_role: BILLING_ROLES;
  costs: Record<string, number>;
};

export type UserBillingData = {
  companyName: string;
  companyTaxNumber: string;
  country: string;
  email: string;
  firstName?: string;
  lastName?: string;
  locality: string;
  mobile: string;
  postalCode: string;
  region: string;
  registrationDate: string;
  street: string;
  organizations: UserOrganizationBillingData[];
  costs: Record<string, number>;
};

export type GetUserBillingDataParams = {
  startDate: string;
  endDate: string;
  userId: string;
  pollingId?: string;
};

export type GetUserBillingDataResponse = UserBillingData;

export type TableBillingUser = {
  email: string;
  name: string;
  id: string;
  registrationDate: string;
  lastSeen: string;
};

export type TableUserOrganizationsBillingData = {
  id: string;
  name: string;
  status: string;
} & Record<string, string>;

export type TableOrganizationsBillingData = {
  id: string;
  name: string;
  status: string;
  billing_role: string;
  billing_admins_str: string;
} & Record<string, string>;

export type TableTotalOrganizationsBillingData = {
  id: "__total";
  name: string;
  status: string;
  billing_role: string;
  billing_admins_str: string;
} & Record<string, string>;

// Organization Billing Data

export type GetOrganizationBillingDataParams = {
  startDate: string;
  endDate: string;
  id: string;
  pollingId?: string;
};

export type OrganizationProjectBillingData = {
  id: string;
  name: string;
  status: string;
  region: string;
  costs: Record<string, number>;
};

export type OrganizationBillingData = {
  id: string;
  name: string;
  status: string;
  billing_admins: string[];
  billing_role: BILLING_ROLES;
  owner: string;
  projects: OrganizationProjectBillingData[];
  costs: Record<string, number>;
};

// export type TableOrganizationBillingData = Omit<
//   OrganizationBillingData,
//   "role"
// > & {
//   role: BILLING_ROLES;
// };

export type GetOrganizationBillingDataResponse = OrganizationBillingData;

export type TableOrganizationProjectBillingData = {
  id: string;
  name: string;
  region: string;
  status: string;
} & Record<string, string>;

// Project Billing Data

export type ProjectBillingData = {
  id: string;
  name: string;
  region: string;
  status: string;
  budget?: {
    limits: { chf: number; eur: number };
    subscribers: string[];
  } | null;
} & Record<string, string>;

export type GetProjectBillingDataParams = {
  // startDate: string;
  // endDate: string;
  id: string;
  orgId: string;
  regionId: string;
  pollingId?: string;
};

export type GetProjectBillingDataResponse = ProjectBillingData;

export type UpdateProjectBudgetLimitParams = {
  regionId: string;
  organizationId: string;
  id: string;
  data: {
    value: number;
    currency: string;
    subscribers: string[];
  };
};

export type UpdateProjectBudgetLimitResponse = ProjectBillingData;

// Instances TAB
export type ProjectBillingInstancesData = {
  id: string;
  name: string;
  region: string;
  status: string;
  items: {
    id: string;
    type: string;
    name?: string;
    deleted?: string; // date
    costs: Record<string, number>;
    itemFlavors: {
      name: string;
      description: string;
      costs: Record<string, number>;
      stopped_time_cost?: {
        hours: number;
        cost: Record<string, number>;
      } | null;
    }[];
  }[];
  costs: Record<string, number>;
};

export type TableBillingInstancesFlavor = {
  id: string;
  name: string;
  description: string;
  stopped_time_hours?: number | null;
  stopped_time_cost?: Record<string, number> | null;
} & Record<string, any>;

export type TableBillingInstancesRecord = {
  id: string;
  type: string;
  name?: string;
  status?: string;
  deleted?: string;
  itemFlavors?: TableBillingInstancesFlavor[];
} & Record<string, any>;

export type TableTotalBillingInstancesRecord = {
  id: string;
  type: string;
  name?: string;
  deletedAt?: string;
  status?: string;
} & Record<string, any>;

export type GetProjectBillingInstancesDataParams = {
  startDate: string;
  endDate: string;
  organizationId: string;
  projectId: string;
  regionId: string;
  pollingId?: string;
};
export type GetProjectBillingInstancesDataResponse =
  ProjectBillingInstancesData;

// Volumes TAB
export type ProjectBillingVolumesData = {
  id: string;
  name: string;
  region: string;
  status: string;
  items: {
    id: string;
    type: string;
    name?: string;
    deleted?: string; // "true" or "false"
    costs: Record<string, number>;
    itemFlavors: {
      name: string;
      description: string;
      costs: Record<string, number>;
    }[];
  }[];
  costs: Record<string, number>;
};

export type TableBillingVolumesRecord = {
  id: string;
  type: string;
  name?: string;
  deleted?: string; // "true" or "false"
  status?: string;
  itemFlavors?: ({
    id: string;
    name: string;
    description: string;
  } & Record<string, string>)[];
} & Record<string, any>;

export type TableTotalBillingVolumesRecord = {
  id: string;
  name?: string;
  status?: string;
  deleted?: string;
  type: string;
} & Record<string, any>;

export type GetProjectBillingVolumesDataParams = {
  startDate: string;
  endDate: string;
  organizationId: string;
  projectId: string;
  regionId: string;
  pollingId?: string;
};
export type GetProjectBillingVolumesDataResponse = ProjectBillingVolumesData;

// DB TAB
export type ProjectBillingDatabasesData = {
  id: string;
  name: string;
  region: string;
  status: string;
  items: {
    id: string;
    name: string;
    deleted: string;
    type: string;
    costs: Record<string, number>;
    itemFlavors: {
      name: string;
      costs: Record<string, number>;
    }[];
  }[];
  costs: Record<string, number>;
};

export type TableBillingDatabasesRecord = {
  id: string;
  name: string;
  type: string;
  deleted?: string;
  status?: string;
  itemFlavors?: ({
    id: string;
    name: string;
  } & Record<string, string>)[];
} & Record<string, any>;

export type TableTotalBillingDatabasesRecord = {
  id: string;
  type: string;
  name: string;
  deleted?: string;
  status?: string;
} & Record<string, any>;

export type GetProjectBillingDatabasesDataParams = {
  startDate: string;
  endDate: string;
  organizationId: string;
  projectId: string;
  regionId: string;
  pollingId?: string;
};
export type GetProjectBillingDatabasesDataResponse =
  ProjectBillingDatabasesData;

// S3 TAB
export type ProjectBillingS3Data = {
  id: string;
  name: string;
  region: string;
  status: string;
  items: {
    id: string;
    type: string;
    name?: string;
    deleted?: string; // "true" or "false"
    costs: Record<string, number>;
    itemFlavors: {
      name: string;
      description: string;
      costs: Record<string, number>;
    }[];
  }[];
  costs: Record<string, number>;
};

export type TableBillingS3Record = {
  id: string;
  type: string;
  name?: string;
  deleted?: string; // "true" or "false"
  status?: string;
  itemFlavors?: ({
    id: string;
    name: string;
  } & Record<string, string>)[];
} & Record<string, any>;

export type TableTotalBillingS3Record = {
  id: string;
  name?: string;
  status?: string;
  deleted?: string;
  type: string;
} & Record<string, any>;

export type GetProjectBillingS3DataParams = {
  startDate: string;
  endDate: string;
  organizationId: string;
  projectId: string;
  regionId: string;
  pollingId?: string;
};
export type GetProjectBillingS3DataResponse = ProjectBillingS3Data;

// Backups TAB
export type ProjectBillingBackupsStorageData = {
  id: string;
  name: string;
  region: string;
  status: string;
  items: {
    id: string;
    type: string;
    name?: string;
    // deleted?: string; // date
    costs: Record<string, number>;
    itemFlavors: {
      name: string;
      description: string;
      id: string;
      size: string;
      created_at: string;
      status?: string; //date of deletion
      costs: Record<string, number>;
    }[];
  }[];
  costs: Record<string, number>;
};

export type TableBillingBackupsStorageRecord = {
  id: string;
  type: string;
  name?: string;
  status?: string;
  // deleted?: string; // not in structure
  itemFlavors?: ({
    name: string;
    size: string;
    id: string;
    created_at: string;
    status: string; //date of deletion
    deleted: string | null;
    description: string;
  } & Record<string, any>)[];
} & Record<string, any>;

export type TableTotalBillingBackupsStorageRecord = {
  id: string;
  type: string;
  name?: string;
  // deletedAt?: string;
  status?: string;
} & Record<string, any>;

export type GetProjectBillingBackupsStorageDataParams = {
  startDate: string;
  endDate: string;
  organizationId: string;
  projectId: string;
  regionId: string;
  pollingId?: string;
};
export type GetProjectBillingBackupsStorageDataResponse =
  ProjectBillingBackupsStorageData;

export type ProjectBillingBackupsLicensesData = {
  id: string;
  name: string;
  region: string;
  status: string;
  items: {
    id: string;
    type: string;
    name?: string;
    // deleted?: string; // date
    costs: Record<string, number>;
    itemFlavors: {
      name: string;
      id: string;
      description: string;
      created_at: string;
      status?: string; //date of deletion
      size: string;
      // costs: Record<string, number>;
    }[];
  }[];
  costs: Record<string, number>;
};

export type TableBillingBackupsLicensesRecord = {
  id: string;
  type: string;
  name?: string;
  status?: string;
  // deleted?: string; // not in structure
  itemFlavors?: {
    name: string;
    size: string;
    id: string;
    created_at: string;
    status: string; //date of deletion
    deleted: string | null;
    description: string;
  }[];
} & Record<string, any>;

export type TableTotalBillingBackupsLicensesRecord = {
  id: string;
  type: string;
  name?: string;
  // deletedAt?: string;
  status?: string;
} & Record<string, any>;

export type GetProjectBillingBackupsLicensesDataParams = {
  startDate: string;
  endDate: string;
  organizationId: string;
  projectId: string;
  regionId: string;
  pollingId?: string;
};
export type GetProjectBillingBackupsLicensesDataResponse =
  ProjectBillingBackupsLicensesData;

// summary tab
export type TableBillingSummaryRecord = {
  id: string; // BILLING_RESOURCES_TYPES (instances, volumes, databases, s3, backups)
  items?: ({
    id: string;
  } & Record<string, any>)[];
} & Record<string, any>;

export type TableTotalBillingSummaryRecord = {
  id: string;
} & Record<string, any>;

export type GenerateProjectBillingDataReportParams = {
  startDate: string;
  endDate: string;
  organizationId: string;
  projectId: string;
  regionId: string;
};

export type GenerateOrgsBillingDataReportParams = {
  startDate: string;
  endDate: string;
  userId: string;
};
